import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit'
import foldersAPI from '@/api/folders'
import { normalizeData } from '@/utils/jsonapi'

/**
 * Entity adapter for folders
 */
const foldersAdapter = createEntityAdapter({
  selectId: (folder) => folder.id,
  sortComparer: (a, b) => {
    const orderDiff = (a.sort_order || 0) - (b.sort_order || 0)
    if (orderDiff !== 0) return orderDiff
    return (a.name || '').localeCompare(b.name || '')
  },
})

/**
 * Async thunk to fetch all folders
 */
export const fetchFolders = createAsyncThunk(
  'folders/fetchAll',
  async (queryOptions = {}, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await foldersAPI.getAll(queryOptions)
      const results = jsonApiResponse.results || jsonApiResponse.data || []
      return normalizeData(results) || []
    } catch (error) {
      const errorMessage = error.apiError?.message || error.message || 'Failed to fetch folders'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Async thunk to create a folder
 */
export const createFolder = createAsyncThunk(
  'folders/create',
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await foldersAPI.create(data)
      return normalizeData(jsonApiResponse.data)
    } catch (error) {
      const errorMessage = error.apiError?.message || error.message || 'Failed to create folder'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Async thunk to update a folder
 */
export const updateFolder = createAsyncThunk(
  'folders/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await foldersAPI.update(id, data)
      return normalizeData(jsonApiResponse.data)
    } catch (error) {
      const errorMessage = error.apiError?.message || error.message || 'Failed to update folder'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Async thunk to delete a folder
 */
export const deleteFolder = createAsyncThunk(
  'folders/delete',
  async (folderId, { rejectWithValue }) => {
    try {
      await foldersAPI.delete(folderId)
      return folderId
    } catch (error) {
      const errorMessage = error.apiError?.message || error.message || 'Failed to delete folder'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Async thunk to reorder items within a folder
 */
export const reorderFolderItems = createAsyncThunk(
  'folders/reorder',
  async ({ folderId, items }, { rejectWithValue }) => {
    try {
      if (folderId) {
        return await foldersAPI.reorder(folderId, items)
      } else {
        return await foldersAPI.reorderRoot(items)
      }
    } catch (error) {
      const errorMessage = error.apiError?.message || error.message || 'Failed to reorder items'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Folders slice
 */
const foldersSlice = createSlice({
  name: 'folders',
  initialState: foldersAdapter.getInitialState({
    loading: false,
    error: null,
  }),
  reducers: {
    clearFoldersError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchFolders.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.loading = false
        foldersAdapter.setAll(state, action.payload || [])
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Create
      .addCase(createFolder.fulfilled, (state, action) => {
        foldersAdapter.addOne(state, action.payload)
      })
      // Update
      .addCase(updateFolder.fulfilled, (state, action) => {
        foldersAdapter.upsertOne(state, action.payload)
      })
      // Delete
      .addCase(deleteFolder.fulfilled, (state, action) => {
        foldersAdapter.removeOne(state, action.payload)
      })
  },
})

export const { clearFoldersError } = foldersSlice.actions

// Base selectors
const getFoldersState = (state) => state.folders || foldersAdapter.getInitialState()

export const {
  selectAll: selectAllFolders,
  selectById: selectFolderById,
  selectIds: selectFolderIds,
} = foldersAdapter.getSelectors(getFoldersState)

export const selectFoldersLoading = (state) => state.folders?.loading || false
export const selectFoldersError = (state) => state.folders?.error || null

/**
 * Select root-level folders (no parent)
 */
export const selectRootFolders = createSelector(
  [selectAllFolders],
  (folders) => folders.filter((f) => !f.parent)
)

/**
 * Select children of a given parent folder
 * @param {string} parentId - Parent folder ID
 */
export const selectFolderChildren = (parentId) =>
  createSelector(
    [selectAllFolders],
    (folders) =>
      folders.filter((f) => {
        const fParent = f.parent?.id || f.parent
        return fParent === parentId
      })
  )

export default foldersSlice.reducer
